/**
 * consent.js
 * Gestión del consentimiento de cookies (art. 22.2 LSSI-CE + RGPD).
 *
 * Regla de oro: ningún script de terceros no necesario puede ejecutarse antes
 * de que el usuario acepte. Por eso PostHog ya NO vive en public/index.html:
 * se inyecta desde aquí y solo si `analytics === true`.
 *
 * Categorías:
 *   - necesarias  → siempre activas, no requieren consentimiento (sesión, JWT,
 *                   preferencia de tema, equipo seleccionado). Son exentas por
 *                   ser imprescindibles para prestar el servicio.
 *   - analiticas  → PostHog. Requieren consentimiento previo y revocable.
 */

const STORAGE_KEY = 'mc_cookie_consent';
const CONSENT_VERSION = 1;

// Si cambian las finalidades o los proveedores hay que subir CONSENT_VERSION
// para volver a pedir el consentimiento: el anterior deja de ser válido.

const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_HOST = 'https://us.i.posthog.com';

/** Lee la decisión guardada. Devuelve null si nunca se decidió o si caducó. */
export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== CONSENT_VERSION) return null; // versión antigua → repreguntar
    return parsed;
  } catch {
    return null;
  }
}

/** true si el usuario ya tomó una decisión (aceptar o rechazar). */
export function hasDecided() {
  return getConsent() !== null;
}

/** true solo si aceptó expresamente la analítica. */
export function analyticsAllowed() {
  return getConsent()?.analytics === true;
}

/**
 * Guarda la decisión y aplica sus efectos inmediatamente.
 * @param {{analytics: boolean}} choices
 */
export function saveConsent({ analytics }) {
  const record = {
    v: CONSENT_VERSION,
    analytics: !!analytics,
    ts: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch { /* almacenamiento no disponible: se respeta la sesión actual */ }

  if (record.analytics) {
    loadAnalytics();
  } else {
    disableAnalytics();
  }
  return record;
}

/** Borra la decisión para volver a mostrar el banner (enlace "Preferencias"). */
export function resetConsent() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* noop */ }
  disableAnalytics();
}

/**
 * Aplica el consentimiento previamente guardado. Se llama una vez al arrancar
 * la app. Si no hay decisión, no carga nada: el banner se encarga de pedirla.
 */
export function applyStoredConsent() {
  if (analyticsAllowed()) loadAnalytics();
}

// ─── PostHog ────────────────────────────────────────────────────────────────

let analyticsLoaded = false;

/**
 * Inyecta PostHog. Solo debe invocarse con consentimiento explícito.
 *
 * `session_recording` va DESACTIVADO a propósito: la app muestra datos de
 * salud de menores (wellness: sueño, fatiga, dolor, estrés) y grabar esas
 * pantallas supondría tratar categorías especiales del art. 9 RGPD sin una
 * base jurídica adecuada. No reactivar sin análisis legal previo.
 */
function loadAnalytics() {
  if (analyticsLoaded || typeof window === 'undefined') return;
  if (window.posthog?.__loaded) return;

  analyticsLoaded = true;

  const script = document.createElement('script');
  script.src = `${POSTHOG_HOST.replace('.i.posthog.com', '-assets.i.posthog.com')}/static/array.js`;
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.onload = () => {
    if (!window.posthog?.init) return;
    window.posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      disable_session_recording: true,
      autocapture: false,          // eventos explícitos, no captura masiva
      capture_pageview: true,
      mask_all_text: true,         // por si se reactivara cualquier captura
      mask_all_element_attributes: true,
      persistence: 'localStorage',
      respect_dnt: true,
    });
  };
  document.head.appendChild(script);
}

/** Corta la recogida y limpia el estado si el usuario revoca. */
function disableAnalytics() {
  try {
    if (window.posthog?.opt_out_capturing) {
      window.posthog.opt_out_capturing();
      window.posthog.reset?.();
    }
  } catch { /* noop */ }
}
